import { mergeAttributes, Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import ResizableImageNode from './ResizableImageNode';

export const CustomImageExtension = Node.create({
  name: 'image',

  addOptions() {
    return {
      inline: false,
      allowBase64: false,
      HTMLAttributes: {},
    };
  },

  inline() {
    return this.options.inline;
  },

  group() {
    return this.options.inline ? 'inline' : 'block';
  },

  draggable: true,

  addAttributes() {
    return {
      src: {
        default: null,
      },
      alt: {
        default: null,
      },
      title: {
        default: null,
      },
      width: {
        default: '100%',
      },
      align: {
        default: 'center', // left, center, right
      },
      style: {
        default: null, // Allow inline styles from AI
      }
    };
  },

  parseHTML() {
    return [
      {
        tag: 'img[src]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    // Convert alignment to CSS styles when saving the final HTML
    let styleString = HTMLAttributes.style || '';
    
    // Add custom width if it's set and not 100%
    if (HTMLAttributes.width && HTMLAttributes.width !== '100%') {
      // Remove any existing width to prevent conflicts
      styleString = styleString.replace(/width:\s*[^;]+;?/g, '');
      styleString += ` width: ${HTMLAttributes.width};`;
    }

    // Apply alignment logic for the final output
    let alignStyles = '';
    if (HTMLAttributes.align === 'center') {
      alignStyles = 'display: block; margin-left: auto; margin-right: auto;';
    } else if (HTMLAttributes.align === 'left') {
      alignStyles = 'float: left; margin-right: 20px;';
    } else if (HTMLAttributes.align === 'right') {
      alignStyles = 'float: right; margin-left: 20px;';
    }

    // Replace display and float rules from AI if we have custom alignment
    if (alignStyles) {
      styleString = styleString.replace(/display:\s*[^;]+;?/g, '');
      styleString = styleString.replace(/float:\s*[^;]+;?/g, '');
      styleString = styleString.replace(/margin(?:-left|-right):\s*[^;]+;?/g, '');
      styleString += ` ${alignStyles}`;
    }

    // We only output standard HTML tags in the final content
    const attrs = mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, { style: styleString.trim() });
    // Remove the custom React attributes that aren't valid HTML
    delete attrs.align;

    return ['img', attrs];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageNode);
  },

  addCommands() {
    return {
      setImage: options => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
          attrs: options,
        })
      },
    }
  },
});
